import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Flag, MagnifyingGlass, Plus, User, Package, Calendar } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { LoadingSpinner } from "@/components/LoadingSpinner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pendente: { label: "Pendente", className: "bg-amber-100 text-amber-700 border-amber-200" },
  em_analise: { label: "Em Análise", className: "bg-sky-100 text-sky-700 border-sky-200" },
  resolvida: { label: "Resolvida", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pendente;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.className}`} data-testid={`complaint-status-${status}`}>
      {cfg.label}
    </span>
  );
};

export default function Reclamacoes() {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");

  useEffect(() => {
    fetchComplaints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fetchComplaints = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "todos") params.status = statusFilter;
      const response = await axios.get(`${API}/complaints`, { params });
      setComplaints(response.data);
    } catch (error) {
      console.error("Error fetching complaints:", error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return complaints;
    const term = search.trim().toLowerCase();
    return complaints.filter(c =>
      (c.client_name || "").toLowerCase().includes(term) ||
      (c.product_name || "").toLowerCase().includes(term)
    );
  }, [complaints, search]);

  const formatDate = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleDateString('pt-PT');
    } catch {
      return "-";
    }
  };

  return (
    <div className="space-y-6 pb-24 md:pb-6" data-testid="reclamacoes-page">
      <PageHeader
        icon={Flag}
        title="Reclamações"
        subtitle="Gestão de reclamações de clientes"
        actions={
          <Link to="/reclamacoes/nova">
            <Button className="bg-[#009392] hover:bg-[#007a7a] text-white" data-testid="btn-nova-reclamacao">
              <Plus size={18} weight="bold" />
              Nova Reclamação
            </Button>
          </Link>
        }
      />

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            placeholder="Pesquisar por cliente ou produto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="reclamacoes-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="reclamacoes-status-filter">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os Estados</SelectItem>
            <SelectItem value="pendente">Pendente</SelectItem>
            <SelectItem value="em_analise">Em Análise</SelectItem>
            <SelectItem value="resolvida">Resolvida</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <LoadingSpinner size={40} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state py-16 bg-stone-50 rounded-xl text-center" data-testid="reclamacoes-empty-state">
          <Flag size={56} className="text-stone-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-stone-700 mb-2">
            {search || statusFilter !== "todos" ? "Nenhuma reclamação encontrada" : "Sem reclamações registadas"}
          </h3>
          <p className="text-stone-500 max-w-md mx-auto">
            {search || statusFilter !== "todos"
              ? "Tente outro filtro ou termo de pesquisa"
              : "As reclamações registadas pelo assistente aparecerão aqui"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reclamacoes-list">
          {filtered.map((c) => (
            <Link
              key={c.id}
              to={`/reclamacoes/${c.id}`}
              className="gresart-card hover:shadow-lg transition-all"
              data-testid={`reclamacao-card-${c.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <h3 className="font-semibold text-stone-900 truncate flex items-center gap-2">
                  <User size={16} className="text-stone-400 flex-shrink-0" />
                  <span className="truncate">{c.client_name || "Cliente sem nome"}</span>
                </h3>
                <StatusBadge status={c.status} />
              </div>
              <div className="space-y-1.5 text-sm text-stone-500">
                <div className="flex items-center gap-2">
                  <Package size={14} className="flex-shrink-0" />
                  <span className="truncate">{c.product_name || "Produto não especificado"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={14} className="flex-shrink-0" />
                  <span>{formatDate(c.created_at)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
